// core/action/trialBalanceActions.js
import { trialBalanceAPI } from './api';
import { toast } from 'react-toastify';

export const trialBalanceActions = {
    // Get trial balance data
    getTrialBalance: async (params = {}) => {
        try {
            const response = await trialBalanceAPI.get(params);
            
            if (response.data) {
                return {
                    success: true,
                    data: response.data
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format',
                    data: {
                        period: {
                            from_date: new Date().toISOString().split('T')[0],
                            to_date: new Date().toISOString().split('T')[0]
                        },
                        summary: {
                            total_ledgers: 0,
                            total_period_debits: 0,
                            total_period_credits: 0,
                            total_opening_debits: 0,
                            total_opening_credits: 0,
                            total_closing_debits: 0,
                            total_closing_credits: 0,
                            is_balanced: true,
                            difference: 0
                        },
                        groups: [],
                        categories: [],
                        ledgers: []
                    }
                };
            }
        } catch (error) {
            console.error('Error fetching trial balance:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch trial balance',
                data: {
                    period: {
                        from_date: new Date().toISOString().split('T')[0],
                        to_date: new Date().toISOString().split('T')[0]
                    },
                    summary: {
                        total_ledgers: 0,
                        total_period_debits: 0,
                        total_period_credits: 0,
                        total_opening_debits: 0,
                        total_opening_credits: 0,
                        total_closing_debits: 0,
                        total_closing_credits: 0,
                        is_balanced: true,
                        difference: 0
                    },
                    groups: [],
                    categories: [],
                    ledgers: []
                }
            };
        }
    },

    // Export trial balance as PDF
    exportToPDF: async (params = {}) => {
        try {
            const response = await trialBalanceAPI.exportPDF(params);
            return response;
        } catch (error) {
            console.error('Error exporting trial balance:', error);
            toast.error('Failed to export trial balance');
            return null;
        }
    },

    // Export trial balance as Excel
    exportToExcel: async (params = {}) => {
        try {
            const response = await trialBalanceAPI.exportExcel(params);
            return response;
        } catch (error) {
            console.error('Error exporting trial balance:', error);
            toast.error('Failed to export trial balance');
            return null;
        }
    },

    // Format currency helper
    formatCurrency: (amount, currency = 'INR') => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    },

    // Format number with commas
    formatNumber: (number, decimals = 2) => {
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(number || 0);
    },

    // Get account group badge class
    getGroupBadgeClass: (group) => {
        const classes = {
            'Asset': 'bg-primary',
            'Liability': 'bg-danger',
            'Equity': 'bg-success',
            'Income': 'bg-info',
            'Expense': 'bg-warning'
        };
        return classes[group] || 'bg-secondary';
    },

    // Get balance type (Dr or Cr) - FIXED: Replaced in_array with JavaScript array includes
    getBalanceType: (balance, group) => {
        if (balance === 0) return '';
        
        // Define arrays for debit-normal and credit-normal accounts
        const debitNormalGroups = ['Asset', 'Expense'];
        const creditNormalGroups = ['Liability', 'Equity', 'Income'];
        
        if (debitNormalGroups.includes(group)) {
            return balance > 0 ? 'Dr' : 'Cr';
        } else if (creditNormalGroups.includes(group)) {
            return balance > 0 ? 'Cr' : 'Dr';
        } else {
            // Default case
            return balance > 0 ? 'Dr' : 'Cr';
        }
    },

    // Helper to check if value is in array (alternative to in_array)
    inArray: (needle, haystack) => {
        return haystack.includes(needle);
    },

    // Get color class based on balance
    getBalanceColorClass: (balance, group) => {
        if (balance === 0) return 'text-muted';
        
        const debitNormalGroups = ['Asset', 'Expense'];
        
        if (debitNormalGroups.includes(group)) {
            return balance > 0 ? 'text-success' : 'text-danger';
        } else {
            return balance > 0 ? 'text-success' : 'text-danger';
        }
    },

    // Format balance with Dr/Cr suffix
    formatBalanceWithType: (balance, group, currency = 'INR') => {
        const type = trialBalanceActions.getBalanceType(balance, group);
        const formatted = trialBalanceActions.formatCurrency(balance, currency);
        return type ? `${formatted} ${type}` : formatted;
    }
};

export default trialBalanceActions;